import { useState, useEffect, useRef } from 'react'
import { ChevronDown, Plus, Database } from 'lucide-react'
import { Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom'
import HomePage from './pages/HomePage'
import SearchPage from './pages/SearchPage'
import AIAnalysisPage from './pages/AIAnalysisPage'
import GenesProteinsPage from './pages/GenesProteinsPage'
import AnalyticsPage from './pages/AnalyticsPage'
import AdminPage from './pages/AdminPage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import UserProfilePage from './pages/UserProfilePage'
import BookmarksPage from './pages/BookmarksPage'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { SessionProvider, useSession } from './contexts/SessionContext'
import { BookmarkProvider } from './contexts/BookmarkContext'
import { CorpusProvider, useCorpus } from './contexts/CorpusContext'
import { userApi } from './api/user'

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  const location = useLocation()
  const isActive = location.pathname === to

  return (
    <Link
      to={to}
      className={`px-2.5 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
        isActive
          ? 'text-brand-600 bg-brand-50'
          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
      }`}
    >
      {children}
    </Link>
  )
}

// Groups the analysis destinations (Analytics, AI Analysis, Genes & Proteins) under
// one dropdown to keep the top bar uncluttered.
function AnalyzeMenu() {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const routes = ['/analytics', '/ai-analysis', '/genes-proteins']
  const isActive = routes.includes(location.pathname)

  // Close on navigation and on outside click.
  useEffect(() => { setOpen(false) }, [location.pathname])
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const analyticsUrl = (() => {
    const lastSearchId = sessionStorage.getItem('pmc_ev_last_search_id')
    const lastFiltered = sessionStorage.getItem('pmc_ev_last_search_filtered')
    return lastSearchId
      ? `/analytics?searchId=${lastSearchId}&filtered=${lastFiltered === 'true'}`
      : '/analytics'
  })()

  const itemCls = 'block px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md'

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center gap-1 px-2.5 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
          isActive
            ? 'text-brand-600 bg-brand-50'
            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
        }`}
      >
        Analyze
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-44 bg-white border rounded-lg shadow-lg p-1 z-50">
          <Link to={analyticsUrl} className={itemCls}>Analytics</Link>
          <Link to="/ai-analysis" className={itemCls}>AI Analysis</Link>
          <Link to="/genes-proteins" className={itemCls}>Genes &amp; Proteins</Link>
        </div>
      )}
    </div>
  )
}

function CorpusPicker() {
  const { isAuthenticated } = useAuth()
  const { corpora, activeCorpusId, setActiveCorpusId } = useCorpus()

  if (!isAuthenticated) return null
  const none = !activeCorpusId

  return (
    <div
      className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 ${
        none ? 'bg-amber-50 border-amber-300' : 'bg-indigo-50 border-indigo-200'
      }`}
      title="Active database — Search queries this corpus"
    >
      <Database className={`w-4 h-4 shrink-0 ${none ? 'text-amber-500' : 'text-indigo-500'}`} />
      <select
        value={activeCorpusId}
        onChange={e => setActiveCorpusId(e.target.value)}
        className={`px-1.5 py-1 border rounded-md text-sm font-medium max-w-[11rem] bg-white ${
          none ? 'text-amber-700' : 'text-slate-800'
        }`}
      >
        <option value="">— Select database —</option>
        {corpora.map(c => (
          <option key={c.corpus_id} value={c.corpus_id}>
            {c.name} ({c.article_count.toLocaleString()})
          </option>
        ))}
      </select>
    </div>
  )
}

function SessionPicker() {
  const { isAuthenticated } = useAuth()
  const { activeCorpusId } = useCorpus()
  const { sessions, activeSessionId, setActiveSessionId, createSession, deleteSession } = useSession()

  if (!isAuthenticated) return null

  const noCorpus = !activeCorpusId

  const handleNew = async () => {
    if (noCorpus) {
      alert('Select a database first — sessions belong to a database.')
      return
    }
    const name = prompt('Name for the new session:')
    if (!name || !name.trim()) return
    try {
      await createSession(name.trim())
    } catch (e: any) {
      alert(e?.message || 'Failed to create session')
    }
  }

  const handleDelete = async () => {
    if (!activeSessionId) return
    if (!confirm('Delete this session? Its datasets are kept but disassociated.')) return
    try {
      await deleteSession(activeSessionId)
    } catch {
      alert('Failed to delete session')
    }
  }

  const noSession = !activeSessionId

  return (
    <div
      className={`flex items-center gap-1 rounded-lg border pl-2 pr-1 py-1 ${
        noCorpus ? 'bg-slate-50 border-slate-200 opacity-60'
          : noSession ? 'bg-amber-50 border-amber-300' : 'bg-brand-50 border-brand-200'
      }`}
      title={
        noCorpus ? 'Select a database first — sessions belong to a database'
          : noSession ? 'No active session — datasets and AI jobs are scoped to a session'
          : 'Active session (datasets and AI jobs are scoped to it)'
      }
    >
      <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${noCorpus ? 'bg-slate-300' : noSession ? 'bg-amber-400 animate-pulse' : 'bg-green-500'}`} />
      <select
        value={activeSessionId}
        onChange={e => setActiveSessionId(e.target.value)}
        disabled={noCorpus}
        className={`px-1.5 py-1 border rounded-md text-sm font-medium max-w-[9rem] bg-white disabled:cursor-not-allowed ${
          noSession ? 'text-amber-700' : 'text-slate-800'
        }`}
      >
        <option value="">{noCorpus ? '— Select a database —' : '— Select session —'}</option>
        {sessions.map(s => (
          <option key={s.session_id} value={s.session_id}>{s.name}</option>
        ))}
      </select>
      <button
        onClick={handleNew}
        disabled={noCorpus}
        className="p-1.5 text-brand-600 hover:bg-brand-100 rounded-md disabled:text-slate-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
        title={noCorpus ? 'Select a database first' : 'Create a new session'}
      >
        <Plus className="w-4 h-4" />
      </button>
      {activeSessionId && (
        <button
          onClick={handleDelete}
          className="px-1.5 py-1 text-sm text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md"
          title="Delete active session"
        >
          ×
        </button>
      )}
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function initialsOf(user: { name?: string | null; email?: string | null } | null): string {
  const name = (user?.name || '').trim()
  if (name) {
    const parts = name.split(/\s+/)
    return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
  }
  return (user?.email || '?').slice(0, 2).toUpperCase()
}

function AuthHeader() {
  const { user, isAuthenticated, isAdmin, logout, checkAuth } = useAuth()
  const { corpora, setActiveCorpusId } = useCorpus()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const handleDefaultDb = async (corpusId: string) => {
    try {
      await userApi.setDefaultCorpus(corpusId || null)
      if (corpusId) setActiveCorpusId(corpusId)
      await checkAuth()  // refresh user.default_corpus_id
    } catch {
      alert('Failed to set default database')
    }
  }

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const handleLogout = () => { setOpen(false); logout(); navigate('/') }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center gap-3 text-sm">
        <Link to="/login" className="font-medium text-slate-600 hover:text-slate-900">Sign in</Link>
        <Link to="/register" className="font-medium text-white bg-brand-600 hover:bg-brand-700 px-3 py-1.5 rounded-lg">Register</Link>
      </div>
    )
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 rounded-full hover:bg-slate-100 pr-1.5"
        title={user?.email || ''}
      >
        <span className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 text-xs font-semibold flex items-center justify-center">
          {initialsOf(user)}
        </span>
        <ChevronDown className="w-4 h-4 text-slate-400" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white border rounded-lg shadow-lg py-1 z-50">
          <div className="px-3 py-2 border-b">
            {user?.name && <div className="text-sm font-medium text-slate-800 truncate">{user.name}</div>}
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
          {corpora.length > 1 && (
            <div className="px-3 py-2 border-b">
              <label className="block text-[11px] font-medium text-slate-500 mb-1">Default database</label>
              <select
                value={user?.default_corpus_id || ''}
                onChange={e => handleDefaultDb(e.target.value)}
                className="w-full px-2 py-1 border rounded-md text-sm bg-white"
              >
                <option value="">— None (ask each login) —</option>
                {corpora.map(c => (
                  <option key={c.corpus_id} value={c.corpus_id}>{c.name}</option>
                ))}
              </select>
              <p className="text-[10px] text-slate-400 mt-1">Selected automatically when you sign in.</p>
            </div>
          )}
          <Link to="/profile" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm text-slate-700 hover:bg-gray-100">Profile &amp; usage</Link>
          {isAdmin && (
            <Link to="/admin" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm text-slate-700 hover:bg-gray-100">Admin dashboard</Link>
          )}
          <div className="border-t my-1" />
          <button onClick={handleLogout} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50">Sign out</button>
        </div>
      )}
    </div>
  )
}

function AppContent() {
  const { isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <Link to="/" className="flex items-center gap-2">
                <img src="/logo.png" alt="PMC AI Search" className="h-10 w-auto" />
                <span className="text-xl font-bold text-slate-800 hidden sm:block">PMC AI Search</span>
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <nav className="flex gap-0.5">
                <NavLink to="/">Home</NavLink>
                <NavLink to="/search">Search</NavLink>
                <AnalyzeMenu />
                <NavLink to="/bookmarks">Bookmarks</NavLink>
              </nav>
              <CorpusPicker />
              <SessionPicker />
              <AuthHeader />
            </div>
          </div>
        </div>
      </header>

      <main className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/" element={<HomePage />} />
            <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/ai-analysis" element={<ProtectedRoute><AIAnalysisPage /></ProtectedRoute>} />
            <Route path="/genes-proteins" element={<ProtectedRoute><GenesProteinsPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
            <Route path="/bookmarks" element={<ProtectedRoute><BookmarksPage /></ProtectedRoute>} />
          </Routes>
        </div>
      </main>
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <CorpusProvider>
        <SessionProvider>
          <BookmarkProvider>
            <AppContent />
          </BookmarkProvider>
        </SessionProvider>
      </CorpusProvider>
    </AuthProvider>
  )
}

export default App
